import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AddHomeResponse,
  ClearIfcFileUploadErrorRequest,
  ClearIfcFileUploadErrorResponse,
  FileParameter,
  IfcFile,
  IfcFileModel,
  IfcParseError,
  IfcType,
  ListHomesRequest,
  GetIfcFileUploadStatusRequest,
  GetIfcFileUploadStatusResponse,
  ListPagedHomeIfcFilesRequest,
  ListPagedHomeIfcFilesResponse,
  ListPagedIfcErrorsRequest,
  ListPagedIfcErrorsResponse,
  TempIfcFileError,
  TempIfcFile
} from 'situview-webapi-client';
import {
  AddHomeRequest,
  BreadcrumbService,
  CustomSituViewWebApiClient,
  HomeService,
  IfcService,
  StateService,
  Utils
} from 'src/app/services';
import { MatSnackBar } from "@angular/material/snack-bar";
import { interval, Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'home-add',
  templateUrl: './home-add.component.html',
  styleUrls: ['./home-add.component.less']
})
export class HomeAddComponent implements OnInit, OnDestroy {
  builder: string = '';
  ifcFiles: IfcFileModel[] = [];
  IfcTypeEnum = IfcType;
  files: FileParameter[] = [];

  homeControl = new FormControl();
  homes: string[] = [];
  selectedHome: string | undefined; 
  filteredHomes: Observable<string[]> | undefined;
  tempIfcFileErrors: TempIfcFileError[] = [];
  tempIfcFiles: TempIfcFile[] = [];
  uploadInProgress: boolean = false;

  ifcFilesLoading: boolean = false;
  homeIfcFiles: IfcFile[] | undefined;
  homeIfcFileErrors: IfcParseError[] | undefined;
  isNotErrorFeed: boolean = true;

  private _subscriptions: Subscription[] = [];
  private _getIfcFileUploadStatusSubscription = new Subscription();
  private _getIfcFileFeedSubscription = new Subscription();
  
  private _showLoading = false;
  public get showLoading(): boolean {
    return this._showLoading;
  }

  public set showLoading(val: boolean) {
    this._showLoading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private situViewClient: CustomSituViewWebApiClient,
    private snackBar: MatSnackBar,
    private stateService: StateService,
    private homeService: HomeService,
    private breadcrumbService: BreadcrumbService,
    private ifcService: IfcService
  ) { }

  ngOnInit(): void {
    this.selectedHome = this.route.snapshot.queryParamMap.get('home') ?? '';
    if (this.selectedHome) {
      this.breadcrumbService.setParam("home", this.capitalizeFirstLetter(this.selectedHome));
      this.homeControl.setValue(this.selectedHome);
      this.homeControl.disable();
    } else {
      this.breadcrumbService.setParam("home", "Add Home");
    }
    this._subscriptions.push(this.stateService.state$.subscribe(x => {
      let companyCode = x.company?.code ?? ""
      if (companyCode && this.builder !== companyCode) {
        this.clearUploadForm();
        this.builder = companyCode;
        let request = new ListHomesRequest();
        request.builder = companyCode;
        this.homeService.ListHomes(request)
          .then(x => {
            this.homes = x.homes ?? [];
            if (this.doesHomeExist(this.selectedHome ?? "")) {
              this.getIfcFileUploadStatus();
              this.loadIfcReportData();
              this.loadIfcReportErrorData();
            }
          });
          this.filteredHomes = this.homeControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterHomes(value))
          );
      };
    }));
  }

  ngOnDestroy(): void {
    if (this._getIfcFileUploadStatusSubscription) {
      this._getIfcFileUploadStatusSubscription.unsubscribe();
    }
    if (this._getIfcFileFeedSubscription) {
      this._getIfcFileFeedSubscription.unsubscribe();
    }
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  capitalizeFirstLetter(text: string) {
    return Utils.capitalizeFirstLetter(text);
  };

  showHomesView() {
    this.router.navigate(['/homes']);
  };

  onUploadClick(buttonNumber: number) {
    let buttonId = "IFCFile_" + buttonNumber;
    document.getElementById(buttonId)?.click();
  };

  onAddIFCFile(ifcFiles: IfcFileModel[]) {
    ifcFiles.push(
      new IfcFileModel(
        {
          fileName: "",
          ifcType: undefined
        }));
  };

  onRemoveIFCFile(ifcFiles: IfcFileModel[], index: number) {
    ifcFiles.splice(index, 1);
  };

  onFileChosen(event: any, ifcFile: IfcFileModel) {
    const file: File = event.target.files[0];

    var fileExt = file.name.split('.').pop();
    if (fileExt !== 'ifc') {
      this.showMessage("Only IFC files are allowed");
      event.target.value = '';
      return;
    }

    ifcFile.fileName = file.name;
    this.files.push({ data: file, fileName: file.name });
  };

  save() {
    this.showLoading = true;
    this.uploadInProgress = true;
    let validationErrors = this.validateForm();

    if (validationErrors !== "") {
      this.showMessage(validationErrors);
      this.showLoading = false;
      this.uploadInProgress = false;
      return;
    }

    var request: AddHomeRequest = new AddHomeRequest();
    request.homeName = this.homeControl?.value?.toLowerCase();
    request.builder = this.builder;
    request.ifcFiles = this.ifcFiles;

    request.ifcFiles.forEach(element => {
      element.fileName = this.cleanupFilePath(element.fileName ?? '');
    });

    if (this.doesHomeExist(this.homeControl?.value?.toLowerCase())) {
      this.situViewClient.uploadFilesForHome(request, this.files)
      .then(response => {
        if (response.success) {
          if (!this.selectedHome) this.selectedHome = this.homeControl?.value?.toLowerCase();
          this.homeControl.setValue(this.selectedHome);
          this.homeControl.disable();
          this.navigateHome();
        } else {
          this.showMessage(`${response.errorMessages?.join("\r\n")}\r\nSave files for home Failed!`);
        }
        this.getIfcFileUploadStatus();
        this.showLoading = false;
        this.uploadInProgress = false;
      }, error => {
        let errorResponse = JSON.parse(error.response);
        this.showMessage(`${errorResponse.errorMessages?.join("\r\n")}\r\nSave files for home Failed!`);
        this.showLoading = false;
        this.uploadInProgress = false;
      });
    } else {
      this.situViewClient.addHome(request, this.files)
      .then(response => {
        if (response.success) {
          this.selectedHome = this.homeControl?.value;
          this.homeControl.setValue(this.selectedHome);
          this.homeControl.disable();
          this.getIfcFileUploadStatus();
          this.pollGetIfcFileUploadStatus();
          this.clearUploadForm();
          this.breadcrumbService.setParam(":home", this.selectedHome);
        } else {
          this.showMessage(`${response.errorMessages?.join("\r\n")}\r\nSave new home Failed!`);
        }
        this.getIfcFileUploadStatus();
        this.showLoading = false;
        this.uploadInProgress = false;
      }, error => {
        let errorResponse: AddHomeResponse = JSON.parse(error.response) as AddHomeResponse;
        this.showMessage(`${errorResponse.errorMessages?.join("\r\n")}\r\nSave new home Failed!`);
        this.showLoading = false;
        this.uploadInProgress = false;
      });
    }
  };

  navigateHome() {
    if (this.doesHomeExist(this.selectedHome ?? "")) {
      this.router.navigate([`/homes`, this.selectedHome]);
    } else {
      this.router.navigate([`/homes/dashboard`]);
    }
  };

  clearError(error: TempIfcFileError) {
    let request = new ClearIfcFileUploadErrorRequest();
    request.builder = this.builder;
    request.homeName = error.home;
    request.id = error.id;

    this.situViewClient.clearIfcFileUploadError(request)
      .then(response => {
        let clearIfcFileUploadErrorResponse = response as ClearIfcFileUploadErrorResponse;
        if (clearIfcFileUploadErrorResponse.success) {
          this.tempIfcFileErrors = this.tempIfcFileErrors.filter(x => x.id !== error.id);
        }
      });
  };

  private pollGetIfcFileUploadStatus() {
    if (this._getIfcFileUploadStatusSubscription) {
      this._getIfcFileUploadStatusSubscription.unsubscribe();
    }
    
    this._getIfcFileUploadStatusSubscription = interval(5000)
      .subscribe(() => {
        this.getIfcFileUploadStatus();
      });
  };

  private getIfcFileUploadStatus() {
    this.showLoading = true;
    let request = new GetIfcFileUploadStatusRequest();
    request.builder = this.builder;
    request.homeName = this.homeControl?.value?.toLowerCase();
    this.situViewClient.getIfcFileUploadStatus(request)
    .then(response => {
      let listIfcFileUploadErrorsResponse = response as GetIfcFileUploadStatusResponse;
      if (listIfcFileUploadErrorsResponse.success) {
        let files = listIfcFileUploadErrorsResponse.tempIfcFiles ?? [];
        let errors = listIfcFileUploadErrorsResponse.tempIfcFileErrors ?? [];
        if (this.tempIfcFiles.length !== files.length || this.tempIfcFileErrors.length !== errors.length) {
            this.loadIfcReportData();
            this.loadIfcReportErrorData();
        }
        this.tempIfcFiles = files;
        this.tempIfcFileErrors = errors;
      }
      this.showLoading = false;
    })
    .catch(err => {
      this.showLoading = false;
    });
  };

  private loadIfcReportData(loadEnvironments: boolean = true) {
    this.ifcFilesLoading = true;

    var request = new ListPagedHomeIfcFilesRequest();
    request.builder = this.builder;
    request.pageNumber = 1;
    request.pageSize = 10;
    request.includeEnvironments = loadEnvironments;
    request.home = this.homeControl?.value;

    this.ifcService.GetIfcReportDataUncached(request)
      .then(response => {
        let listPagedHomeIfcFilesResponse = response as ListPagedHomeIfcFilesResponse;
        this.homeIfcFiles = listPagedHomeIfcFilesResponse.items;
        this.ifcFilesLoading = false;
      })
      .catch(err => {
        this.ifcFilesLoading = false;
      });
  };

  private loadIfcReportErrorData() {
    this.ifcFilesLoading = true;

    var request = new ListPagedIfcErrorsRequest();
    request.builder = this.builder;
    request.pageNumber = 1;
    request.pageSize = 10;
    request.home = this.homeControl?.value;

    this.ifcService.GetIfcReportErrorDataUncached(request)
      .then(response => {
        let listPagedHomeIfcFilesResponse = response as ListPagedIfcErrorsResponse;
        this.homeIfcFileErrors = listPagedHomeIfcFilesResponse.items;
        this.ifcFilesLoading = false;
      })
      .catch(err => {
        this.ifcFilesLoading = false;
      });
  };

  private validateForm(): string {
    var errors: string[] = [];

    if (!this.homeControl?.value) {
      errors.push("You need to specify the new home name");
    }

    if (this.ifcFiles.length === 0) {
      errors.push("You need to select at least one IFC File");
    }
    else {
      var index: number = 0;
      this.ifcFiles.forEach(element => {
        index++;
        var fileError: string = "";
        var typeError: string = "";

        if (element.fileName === "") {
          fileError = "File must be specified.";
        }
        if (element.ifcType === null) {
          typeError = "IFC Type must be specified.";
        }

        if (fileError !== "" || typeError !== "") {
          errors.push(`IFC File #${index}: ${fileError} ${typeError}`);
        }
      });
    }

    return errors.join('\r\n');
  };

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000,
      panelClass: ['snackbar-panel']
    });
  };

  private cleanupFilePath(filePath: string): string {
    var cleanedFilePath = filePath;

    if (filePath.match(/fakepath/)) {
      cleanedFilePath = filePath.replace(/C:\\fakepath\\/i, '');
    }

    return cleanedFilePath;
  };

  private doesHomeExist(homeName: string): boolean {
    if (!this.homes || !homeName) return false;
    return this.homes.map(x => x.toLowerCase()).indexOf(homeName.toLowerCase()) !== -1;
  };

  private _filterHomes(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.homes.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
  };

  private clearUploadForm() {
    this.ifcFiles = [];
    this.ifcFiles.push(
      new IfcFileModel(
        {
          fileName: "",
          ifcType: undefined
        }));
  };
}
